/*
  Noma header: search panel and mobile menu panel.
  Both panels are <details data-noma-panel> elements in sections/noma-header.liquid and already open / close without JavaScript;
  this adds what <details> does not do by itself: Escape, a click outside, only one panel open at a time, the search field gets the
  focus on open, aria-expanded on the buttons, the menu closes when the window grows to the desktop layout (and on back / forward
  cache restores), and the measured header height (--noma-header-h, used for the menu panel height and anchor scrolling).
*/
if (!customElements.get('noma-header')) {
  customElements.define(
    'noma-header',
    class NomaHeader extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector('.noma-header__bar');
        this.panels = Array.from(this.querySelectorAll('details[data-noma-panel]'));

        this.onToggle = (event) => this.handleToggle(event.currentTarget);
        this.onKeydown = (event) => this.handleKeydown(event);
        this.onDocumentClick = (event) => this.handleDocumentClick(event);
        this.onPageShow = (event) => {
          // back / forward cache: do not come back to an open panel
          if (event.persisted) this.panels.forEach((panel) => this.closePanel(panel));
        };
        this.onDesktop = (event) => {
          if (event.matches) this.closePanel(this.querySelector('details[data-noma-panel="menu"]'));
        };

        this.panels.forEach((panel) => {
          panel.addEventListener('toggle', this.onToggle);
          this.syncExpanded(panel);
        });
        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('pageshow', this.onPageShow);

        this.desktopQuery = window.matchMedia('(min-width: 990px)');
        if (this.desktopQuery.addEventListener) this.desktopQuery.addEventListener('change', this.onDesktop);

        this.measure();
        if ('ResizeObserver' in window && this.bar) {
          this.resizeObserver = new ResizeObserver(() => this.measure());
          this.resizeObserver.observe(this.bar);
        }
      }

      disconnectedCallback() {
        this.panels.forEach((panel) => panel.removeEventListener('toggle', this.onToggle));
        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('pageshow', this.onPageShow);
        if (this.desktopQuery && this.desktopQuery.removeEventListener) {
          this.desktopQuery.removeEventListener('change', this.onDesktop);
        }
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      measure() {
        if (!this.bar) return;
        const height = Math.round(this.bar.getBoundingClientRect().height) + 1; // + the 1px bottom border
        document.documentElement.style.setProperty('--noma-header-h', `${height}px`);
      }

      syncExpanded(panel) {
        const summary = panel.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', panel.open ? 'true' : 'false');
      }

      closePanel(panel, returnFocus) {
        if (!panel || !panel.open) return;
        panel.open = false;
        this.syncExpanded(panel);
        if (returnFocus) {
          const summary = panel.querySelector('summary');
          if (summary) summary.focus();
        }
      }

      handleToggle(panel) {
        this.syncExpanded(panel);
        if (!panel.open) return;
        this.panels.forEach((other) => {
          if (other !== panel) this.closePanel(other);
        });
        const field = panel.querySelector('input[type="search"]');
        if (field) field.focus();
      }

      handleKeydown(event) {
        if (event.key !== 'Escape') return;
        this.panels.forEach((panel) => {
          if (!panel.open) return;
          const focusInside = panel.contains(document.activeElement);
          this.closePanel(panel, focusInside);
        });
      }

      handleDocumentClick(event) {
        this.panels.forEach((panel) => {
          if (panel.open && !panel.contains(event.target)) this.closePanel(panel);
        });
      }
    }
  );
}
